import { gql } from '@apollo/client';
import { Highlighter, Menu, Typeahead } from 'react-bootstrap-typeahead';
import styled from 'styled-components';
import { sortBy } from 'lodash';
import { Button } from 'reactstrap';
import { useLocation } from 'react-router';
import { useQueryWithMsg } from '../../utils/hooks';
import {
  farmNameColumn,
  farmOwnerColumn,
  farmTeamColumn,
  farmUpdatedAtColumn,
} from '../../utils/columns';
import ItemsTable from '../tables/ItemsTable';
import { useMultiProjectDashboardContext } from '../../contexts/MultiProjectDashboardContext';
import NewTabIcon from '../icons/NewTabIcon';
import MinusIcon from '../icons/MinusIcon';
import PlusIcon from '../icons/PlusIcon';
import SearchIcon from '../icons/SearchIcon';
import { StyledNameDiv } from '../left-menu/menu-components';
import { captureEvent } from '../../utils/analytics';
import { GreyPill } from '../form-components/RoundedGreyContainer';
import {
  getBestMatchForHighlight,
  getNGrams,
  searchBy,
} from '../../utils/search';
import { augmentFarmTags } from '../../utils/utils';
import useNavigateToFarm from '../../hooks/useNavigateToFarm';
import { FarmColumnsFragment } from '../../constants/fragments';

const SearchTypeahead = styled(Typeahead)`
  .rbt-menu {
    min-width: fit-content;
  }
  .rbt-input {
    width: 400px;
    padding-left: 2rem;
    @media (max-width: 768px) {
      max-width: calc(100vw - 2 * var(--content-x-padding));
    }
  }
`;

const MenuTable = styled(ItemsTable)`
  width: 100%;
  // Table is at least the width of the search input,
  // but otherwise should not overlap dashboard right menu
  min-width: 400px;
  max-width: calc(100vw - (2 * var(--side-panel-width)) - 40px);
  &,
  table {
    max-height: 298px;
  }
  table {
    border: none;
    margin-bottom: 0;
    width: 100%;
  }
  th {
    border: none;
    color: var(--text-color);
    font-size: 13px;
    font-weight: bold;
    width: 100%;
  }
  tr:hover .row-hover {
    color: var(--text-color);
  }
  td {
    border: none;
    color: var(--text-color);
    font-size: 14px;
    padding: 4px 8px;
    vertical-align: middle;
  }
`;

const TableButton = styled(Button)`
  line-height: inherit;
  padding: 0;
`;

const MaxWidthNameDiv = styled(StyledNameDiv)`
  max-width: 250px;
`;

const ContainerWithGap = styled.div`
  gap: 4px;
`;

const AccessibleProjectsQuery = gql`
  ${FarmColumnsFragment}
  query AccessibleProjects {
    accessibleFarms {
      ...FarmColumnFields
    }
  }
`;

const filterBy = (option, props) => {
  // All of the words passed in must match either the project, team name, or one of the tags
  return searchBy(['name', 'team.name', 'tags'])(props.text)(option);
};

// eslint-disable-next-line react/display-name
const renderMenu = (dashboardFarmsMap, toggleDashboardFarm, linkToFarm) => (
  results,
  menuProps,
  state,
) => {
  const allNGrams = getNGrams(state.text);
  const getBestMatch = (stringToMatch) =>
    getBestMatchForHighlight(allNGrams)(stringToMatch);

  // Action column
  const formatAction = (inDashboard, farm) => (
    <TableButton
      color="link"
      className="row-hover"
      onClick={(e) => {
        e.preventDefault();
        // prevent click on this button from triggering the row click
        e.stopPropagation();
        toggleDashboardFarm(inDashboard, farm);
      }}
    >
      {inDashboard ? <MinusIcon /> : <PlusIcon />}
    </TableButton>
  );
  const actionCol = {
    accessor: (item) => item.id in dashboardFarmsMap,
    displayString: '',
    format: formatAction,
  };

  // Name column
  const formatName = (name) => (
    <MaxWidthNameDiv title={name}>
      <Highlighter search={getBestMatch(name)}>{name}</Highlighter>
    </MaxWidthNameDiv>
  );
  const nameCol = {
    ...farmNameColumn,
    format: formatName,
  };

  // View farm column
  const formatView = (farmId) => (
    <TableButton
      color="link"
      onClick={(e) => {
        e.stopPropagation();
        captureEvent('farm-opened', {
          location: 'multi project dashboard search list',
          inNewTab: true,
        });
        linkToFarm(farmId);
      }}
    >
      <NewTabIcon />
    </TableButton>
  );
  const viewCol = {
    accessor: 'id',
    displayString: '',
    format: formatView,
  };

  // Team column
  const formatTeam = (team) =>
    team ? (
      <Highlighter search={getBestMatch(team)}>{team}</Highlighter>
    ) : (
      <i>None</i>
    );
  const teamCol = {
    ...farmTeamColumn,
    format: formatTeam,
  };

  const formatTags = (tags, { id }) => {
    return (
      <ContainerWithGap className="d-flex">
        {tags.map((tag) => (
          <GreyPill
            key={`${id}-${tag}`}
            title={tag}
            background={(tag === 'Locked' || tag === 'Archived') && '#d6d6d6'}
          >
            <Highlighter search={getBestMatch(tag)}>{tag}</Highlighter>
          </GreyPill>
        ))}
      </ContainerWithGap>
    );
  };

  const tagCol = {
    accessor: 'tags',
    displayString: 'Tags',
    format: formatTags,
    hideIfAllMatchFn: (value) => !value?.length,
  };

  const cols = [
    actionCol,
    nameCol,
    viewCol,
    farmUpdatedAtColumn,
    teamCol,
    farmOwnerColumn,
    tagCol,
  ];

  const getItems = (farms) =>
    farms.map((farm) => ({
      ...farm,
      onClick: () => toggleDashboardFarm(farm.id in dashboardFarmsMap, farm),
    }));

  const { paginationOption } =
    // eslint-disable-next-line react/destructuring-assignment -- `results` is not props
    results.length > 0 && results[results.length - 1];

  return (
    <Menu className="p-0" {...menuProps}>
      <MenuTable
        // eslint-disable-next-line react/destructuring-assignment -- `results` is not props
        data={paginationOption ? results.slice(0, -1) : results}
        getItems={getItems}
        cols={cols}
        selectedIds={Object.keys(dashboardFarmsMap)}
        hover
        footer={
          paginationOption && (
            <tr>
              <td colSpan="8" className="p-2 font-small text-muted">
                Only the first 100 search results are shown. Try narrowing your
                search to see other results.
              </td>
            </tr>
          )
        }
      />
    </Menu>
  );
};

const DashboardSearch = () => {
  const { data, msg } = useQueryWithMsg(AccessibleProjectsQuery);
  const {
    dashboards: { dashboardFarmsMap, addDashboardFarm, removeDashboardFarm },
  } = useMultiProjectDashboardContext();

  const sortedFarms = sortBy(data?.accessibleFarms || [], (f) =>
    f.name.toLowerCase(),
  );
  // add locked and/or archived to the tags for each farm
  const augmentedFarms = sortedFarms.map((farm) => augmentFarmTags(farm));

  const navigateToFarm = useNavigateToFarm();
  const { pathname } = useLocation();
  const linkToFarm = (farmId) =>
    navigateToFarm(farmId, { state: { source: pathname } }, true);

  const toggleDashboardFarm = (inDashboard, farm) => {
    if (inDashboard) {
      captureEvent('dashboard-farm-removed', { location: 'farm-search' });
      removeDashboardFarm(farm.id);
    } else {
      captureEvent('dashboard-farm-added');
      addDashboardFarm(farm);
    }
  };

  return (
    <div className="d-flex m-2 align-items-center">
      <SearchTypeahead
        size="sm"
        id="projects-search"
        filterBy={filterBy}
        options={augmentedFarms}
        placeholder={msg || 'Filter by project name, team, or tags...'}
        renderMenu={renderMenu(
          dashboardFarmsMap,
          toggleDashboardFarm,
          linkToFarm,
        )}
      />
      <SearchIcon
        className="position-absolute ml-2 text-muted"
        width={16}
        height={16}
      />
    </div>
  );
};

export default DashboardSearch;
